import { useCallback } from "react";
import type { Route } from "next";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import { MENU_SIDEBAR } from "@/config/layout-30.config";
import {
  AccordionMenu,
  AccordionMenuGroup,
  AccordionMenuItem,
  AccordionMenuLabel,
} from "@/components/ui/accordion-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLayout } from "./context";
import { Navbar } from "./navbar";

export function SidebarMenu({ onNavigate }: { onNavigate?: () => void }) {
  const t = useTranslations("layout30");
  const pathname = usePathname();
  const { isMobile } = useLayout();
  const matchPath = useCallback(
    (path: string): boolean =>
      path === pathname || (path.length > 1 && pathname.startsWith(path)),
    [pathname],
  );

  return (
    <aside
      data-slot="sidebar-menu-wrapper"
      aria-label={t("sectionNavigation")}
      className="lg:fixed lg:z-10 lg:top-(--header-height) lg:bottom-0 flex flex-col items-stretch lg:border-e border-border bg-background p-2.5 lg:p-3.5 w-[250px] lg:w-(--sidebar-menu-width)"
    >
      {isMobile ? <Navbar /> : null}
      <ScrollArea className="grow h-[calc(100vh-6rem)] lg:h-[calc(100vh-4rem)] pe-2.5 -me-2.5">
        <AccordionMenu
          selectedValue={pathname}
          matchPath={matchPath}
          type="multiple"
          className="space-y-5"
          classNames={{
            separator: "-mx-2 mb-2.5",
            label: "text-xs font-normal text-muted-foreground",
            item: "h-8.5 px-2.5 text-sm font-normal text-foreground hover:text-primary data-[selected=true]:bg-muted data-[selected=true]:font-medium data-[selected=true]:text-foreground [&[data-selected=true]_svg]:opacity-100",
            group: "",
          }}
        >
          {MENU_SIDEBAR.map((item) => (
            <AccordionMenuGroup key={item.title}>
              <AccordionMenuLabel>
                {t(item.title! as Parameters<typeof t>[0])}
              </AccordionMenuLabel>
              {item.children?.map((child) => (
                <AccordionMenuItem
                  key={child.title}
                  value={child.path ?? child.title!}
                  disabled={child.disabled}
                  asChild={Boolean(child.path)}
                >
                  {child.path ? (
                    <Link href={child.path as Route} onClick={onNavigate}>
                      {child.icon ? <child.icon /> : null}
                      <span>{t(child.title! as Parameters<typeof t>[0])}</span>
                    </Link>
                  ) : (
                    <>
                      {child.icon ? <child.icon /> : null}
                      <span>{t(child.title! as Parameters<typeof t>[0])}</span>
                    </>
                  )}
                </AccordionMenuItem>
              ))}
            </AccordionMenuGroup>
          ))}
        </AccordionMenu>
      </ScrollArea>
    </aside>
  );
}
